import React, { createContext, useContext, useEffect, useState } from "react";
import { getPref, setPref } from "../lib/prefs";

const ThemeContext = createContext({ theme: "light", setTheme: () => {}, resolved: "light" });

const PREF_KEY = "theme";

function applyTheme(resolved) {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.setAttribute("data-theme", resolved);
  // Update <meta name="theme-color"> for mobile chrome / iOS status bar
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#0D1117" : "#F9F8F6");
}

function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const saved = getPref(PREF_KEY);
    if (saved === "light" || saved === "dark" || saved === "system") return saved;
    return "dark"; // default to premium dark per product direction
  });
  const [resolved, setResolved] = useState("light");

  useEffect(() => {
    const r = theme === "system" ? getSystemTheme() : theme;
    setResolved(r);
    applyTheme(r);
    setPref(PREF_KEY, theme);
  }, [theme]);

  // React to system changes when in "system" mode
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const r = mql.matches ? "dark" : "light";
      setResolved(r);
      applyTheme(r);
    };
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState, resolved }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
